import React, { useState, useMemo, ChangeEvent, createContext } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faCopy } from '@fortawesome/free-solid-svg-icons';
import AdvanceTable from 'components/base/AdvanceTable';
import RevealDropdown, { RevealDropdownTrigger } from 'components/base/RevealDropdown';
import ApiTokenTableActionDropdownItems from './ApiTokenTableActionDropdown';
import ApiTokenAddForm from './ApiTokenAddForm';
import ApiTokenEditForm from './ApiTokenEditForm';
import ApiTokenDeleteModal from './ApiTokenDeleteModal';
import SearchBox from 'components/common/SearchBox';
import useAdvanceTable from 'hooks/common/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import {
  ApiToken,
  CreateApiTokenRequest,
  MAX_API_TOKENS_PER_USER,
  UpdateApiTokenRequest,
} from 'models/ApiToken';
import Button from 'components/base/Button';
import Badge from 'components/base/Badge';
import { Alert, Button as BsButton, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { daysUntilExpiry, formatApiTokenDate } from 'utils/apiTokenDatetime';
import { DATA_ACCESS_PATHS } from 'shiplab-components/data-access/paths';
import { DataAccessTabHeader } from 'shiplab-components/data-access/dataAccessTypography';

export type ApiTokenContextType = {
  handleEdit: (apiToken: ApiToken) => void;
  handleDelete: (apiToken: ApiToken) => void;
};

export const ApiTokenContext = createContext<ApiTokenContextType | null>(null);

const NOTIFICATION_DISMISS_MS = 2000;

type ApiTokenValueCellProps = {
  token: string;
  visible: boolean;
  onToggle: () => void;
  onCopy: (text: string) => void;
};

const MASKED_TOKEN_MIN_DOTS = 32;
const MASKED_TOKEN_MAX_DOTS = 42;

const ApiTokenValueCell: React.FC<ApiTokenValueCellProps> = ({
  token,
  visible,
  onToggle,
  onCopy,
}) => {
  const maskedToken =
    token &&
    '•'.repeat(
      Math.min(Math.max(token.length, MASKED_TOKEN_MIN_DOTS), MASKED_TOKEN_MAX_DOTS),
    );
  const displayToken = visible ? token || '—' : maskedToken || '—';

  return (
    <div className="d-inline-flex align-items-center flex-nowrap" style={{ maxWidth: '100%' }}>
      <span
        className="font-monospace text-900 text-truncate"
        style={{
          fontSize: '0.875rem',
          minWidth: 0,
          flex: '0 1 auto',
          maxWidth: visible ? '36rem' : undefined,
          whiteSpace: 'nowrap',
        }}
      >
        {displayToken}
      </span>
      <div className="d-flex flex-shrink-0 align-items-center gap-1 ms-2">
        <BsButton
          variant="outline-primary"
          size="sm"
          onClick={onToggle}
          aria-label="Toggle token visibility"
        >
          <FontAwesomeIcon icon={visible ? faEyeSlash : faEye} />
        </BsButton>
        <BsButton
          variant="outline-success"
          size="sm"
          onClick={(e) => {
            onCopy(token);
            e.currentTarget.blur();
          }}
          disabled={!token}
          aria-label="Copy token"
        >
          <FontAwesomeIcon icon={faCopy} />
        </BsButton>
      </div>
    </div>
  );
};

type ApiTokenManagementPanelProps = {
  apiTokens: ApiToken[];
  loading: boolean;
  error: string | null;
  isAtTokenLimit: boolean;
  createToken: (data: CreateApiTokenRequest) => Promise<ApiToken>;
  updateToken: (id: number, data: UpdateApiTokenRequest) => Promise<ApiToken>;
  deleteToken: (id: number) => Promise<void>;
};

const ApiTokenManagementPanel: React.FC<ApiTokenManagementPanelProps> = ({
  apiTokens,
  loading,
  error,
  isAtTokenLimit,
  createToken,
  updateToken,
  deleteToken,
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [notification, setNotification] = useState<string | null>(null);
  const [editingToken, setEditingToken] = useState<ApiToken | null>(null);
  const [deletingToken, setDeletingToken] = useState<ApiToken | null>(null);
  const [visibleTokenIds, setVisibleTokenIds] = useState<Set<number>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  const toggleTokenVisibility = (id: number) => {
    setVisibleTokenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const copyToClipboard = (text: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setNotification('Copied to clipboard!');
    setTimeout(() => setNotification(null), NOTIFICATION_DISMISS_MS);
  };

  const handleAddToken = async (data: CreateApiTokenRequest) => {
    const created = await createToken(data);
    setNotification(`API token created: ${created.description}`);
    setTimeout(() => setNotification(null), NOTIFICATION_DISMISS_MS);
  };

  const handleEdit = (apiToken: ApiToken) => {
    setEditingToken(apiToken);
  };

  const handleDelete = (apiToken: ApiToken) => {
    setDeletingToken(apiToken);
  };

  const handleUpdateToken = async (id: number, data: UpdateApiTokenRequest) => {
    await updateToken(id, data);
  };

  const handleConfirmDelete = async (id: number) => {
    await deleteToken(id);
    setNotification('API token deleted');
    setTimeout(() => setNotification(null), NOTIFICATION_DISMISS_MS);
  };

  const columns: ColumnDef<ApiToken>[] = useMemo(
    () => [
      {
        accessorKey: 'description',
        header: 'Description',
        cell: ({ row }) => (
          <div className="d-flex align-items-center gap-2">
            <span>{row.original.description}</span>
            {row.original.is_expired && (
              <Badge bg="secondary" variant="phoenix">
                Expired
              </Badge>
            )}
          </div>
        ),
        meta: {
          headerProps: { style: { width: '15%' } },
        },
      },
      {
        accessorKey: 'token',
        header: 'API Key',
        cell: ({ row }) => (
          <ApiTokenValueCell
            token={row.original.token}
            visible={visibleTokenIds.has(row.original.id)}
            onToggle={() => toggleTokenVisibility(row.original.id)}
            onCopy={copyToClipboard}
          />
        ),
        meta: {
          headerProps: { style: { width: '48%' } },
          cellProps: { className: 'white-space-nowrap', style: { maxWidth: 0 } },
        },
      },
      {
        accessorKey: 'expires_at',
        header: 'Expires',
        cell: ({ row }) => {
          const { expires_at, is_expired } = row.original;
          const daysRemaining = !is_expired ? daysUntilExpiry(expires_at) : null;
          return (
            <div>
              <div>{formatApiTokenDate(expires_at)}</div>
              {daysRemaining !== null && (
                <div className="text-700 fs-10">
                  {daysRemaining === 0 ? 'Expires today' : `${daysRemaining} days remaining`}
                </div>
              )}
            </div>
          );
        },
        meta: {
          headerProps: { style: { width: '16%' }, className: 'pe-1' },
          cellProps: { className: 'pe-1' },
        },
      },
      {
        accessorKey: 'created_at',
        header: 'Created',
        cell: ({ row }) => formatApiTokenDate(row.original.created_at),
        meta: {
          headerProps: { style: { width: '14%' }, className: 'ps-1' },
          cellProps: { className: 'ps-1 white-space-nowrap' },
        },
      },
      {
        id: 'action',
        header: 'Actions',
        cell: ({ row }) => (
          <RevealDropdownTrigger className="d-flex justify-content-end w-100">
            <RevealDropdown className="position-static">
              <ApiTokenTableActionDropdownItems apiToken={row.original} />
            </RevealDropdown>
          </RevealDropdownTrigger>
        ),
        meta: {
          headerProps: { style: { width: '7%' } },
          cellProps: { className: 'text-end' },
        },
      },
    ],
    [visibleTokenIds],
  );

  const table = useAdvanceTable({
    data: apiTokens,
    columns,
    pageSize: MAX_API_TOKENS_PER_USER,
    pagination: true,
    sortable: true,
  });

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    table.setGlobalFilter(e.target.value || undefined);
  };

  const totalCount = apiTokens.length;
  const filteredCount = table.getFilteredRowModel().rows.length;
  const isSearching = searchQuery.trim().length > 0;
  const expiredCount = apiTokens.filter((token) => token.is_expired).length;
  const remainingCount = MAX_API_TOKENS_PER_USER - totalCount;

  const contextValue = {
    handleEdit,
    handleDelete,
  };

  return (
    <ApiTokenContext.Provider value={contextValue}>
      <div>
        <DataAccessTabHeader title="API Tokens">
          Create and manage API tokens for the Shiplab REST API and MCP server. For endpoint details,
          request examples, and MCP setup guides, see the{' '}
          <Link to={DATA_ACCESS_PATHS.restApi}>REST API</Link> and{' '}
          <Link to={DATA_ACCESS_PATHS.mcpServer}>MCP Server</Link> tabs.
        </DataAccessTabHeader>

        {notification && (
          <Alert variant="outline-success" onClose={() => setNotification(null)} dismissible>
            {notification}
          </Alert>
        )}

        {error && (
          <Alert variant="outline-danger" className="mb-4">
            {error}
          </Alert>
        )}

        <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center gap-3 mb-4">
          <Button
            className="addBtn"
            variant="primary"
            onClick={() => setShowAddForm(true)}
            disabled={isAtTokenLimit}
          >
            + Create Token
          </Button>
          {isAtTokenLimit && (
            <span className="text-700 fs-9">
              Maximum of {MAX_API_TOKENS_PER_USER} API tokens reached. Delete a token to create a
              new one.
            </span>
          )}
          <SearchBox
            placeholder="Search..."
            size="sm"
            onChange={handleSearchInputChange}
            className="ms-md-auto"
          />
        </div>

        {loading ? (
          <div className="d-flex justify-content-center py-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading…</span>
            </Spinner>
          </div>
        ) : (
          <AdvanceTableProvider {...table}>
            <AdvanceTable
              tableProps={{
                size: 'sm',
                className: 'phoenix-table fs-9 mb-0 border-top border-200',
                style: { tableLayout: 'fixed', width: '100%' },
              }}
              rowClassName="hover-actions-trigger position-static"
            />
            <div className="d-flex align-items-center justify-content-between border-top border-200 py-2 fs-9">
              <span className="text-700">
                {isSearching
                  ? `Showing ${filteredCount} of ${totalCount} tokens`
                  : `${totalCount} of ${MAX_API_TOKENS_PER_USER} tokens in use`}
              </span>
              <span className="text-600">
                {isSearching
                  ? expiredCount > 0 && `${expiredCount} expired`
                  : remainingCount > 0
                    ? `${remainingCount} remaining`
                    : expiredCount > 0
                      ? `${expiredCount} expired`
                      : 'Limit reached'}
              </span>
            </div>
          </AdvanceTableProvider>
        )}

        <ApiTokenAddForm
          key={formKey}
          show={showAddForm}
          handleClose={() => {
            setShowAddForm(false);
            setFormKey((prev) => prev + 1);
          }}
          handleSave={handleAddToken}
        />

        {editingToken && (
          <ApiTokenEditForm
            apiToken={editingToken}
            onCancel={() => setEditingToken(null)}
            handleUpdate={handleUpdateToken}
            onSaved={() => {
              setEditingToken(null);
              setNotification(`API token updated: ${editingToken.description}`);
              setTimeout(() => setNotification(null), NOTIFICATION_DISMISS_MS);
            }}
          />
        )}

        <ApiTokenDeleteModal
          show={Boolean(deletingToken)}
          apiToken={deletingToken}
          onCancel={() => setDeletingToken(null)}
          onConfirm={handleConfirmDelete}
        />
      </div>
    </ApiTokenContext.Provider>
  );
};

export default ApiTokenManagementPanel;
